import { useCallback, useEffect, useState } from 'react'
import { isRejected } from '@reduxjs/toolkit'
import { useDispatch, useSelector } from 'react-redux'
import { MembershipPerk, MembershipTier } from '@dataTypes/membership.type'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { AppDispatch, RootState } from '@store/index'
import {
  createMembershipTier,
  getAllMembershipTiers,
  removeMembershipTier,
  updateMembershipTier,
  updateMembershipTierStatus,
} from '@store/actions/membership.action'

const MEMBERSHIP_LOADING_CONTAINER = '.settings-membership'

export const useMembership = (): {
  tiers: MembershipTier[]
  loading: boolean
  dataReady: boolean
  saving: boolean
  error: string | null
  refreshTiers: () => void
  saveTier: (
    tier: MembershipTier,
    perks: MembershipPerk[],
    mode: 'add' | 'edit',
  ) => Promise<boolean>
  deleteTier: (tierId: string) => Promise<boolean>
  toggleTierStatus: (tierId: string, enabled: boolean) => Promise<boolean>
} => {
  const dispatch = useDispatch<AppDispatch>()
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { tiers, loading, saving, error } = useSelector(
    (state: RootState) => state.membership,
  )
  const selectedClinicId = useSelector(
    (state: RootState) => state.clinic.selectedClinic?.clinic_id,
  )
  const [dataReady, setDataReady] = useState(false)

  useSettingsSubpageLoading(!dataReady)

  const refreshTiers = useCallback((): void => {
    void dispatch(getAllMembershipTiers()).finally(() => {
      setDataReady(true)
    })
  }, [dispatch])

  useEffect(() => {
    setDataReady(false)
    refreshTiers()
  }, [refreshTiers, selectedClinicId])

  useEffect(() => {
    if (saving) {
      showLoading({ container: MEMBERSHIP_LOADING_CONTAINER })
      return
    }

    hideLoading(MEMBERSHIP_LOADING_CONTAINER)
  }, [hideLoading, saving, showLoading])

  const saveTier = useCallback(
    async (
      tier: MembershipTier,
      perks: MembershipPerk[],
      mode: 'add' | 'edit',
    ): Promise<boolean> => {
      const action =
        mode === 'add'
          ? createMembershipTier({ tier, perks })
          : updateMembershipTier({ tier, perks })

      const result = await dispatch(action)

      if (isRejected(result)) {
        showToast(
          createFailedToast(
            'Unable to save',
            (result.payload as string) || 'Failed to save membership tier.',
          ),
        )
        return false
      }

      showToast(
        createSuccessToast(
          mode === 'add' ? 'New membership tier' : 'Tier updated',
          mode === 'add'
            ? `\u201C${tier.name}\u201D membership tier has been added.`
            : `${tier.name} has been saved successfully.`,
        ),
      )
      return true
    },
    [dispatch, showToast],
  )

  const deleteTier = useCallback(
    async (tierId: string): Promise<boolean> => {
      const result = await dispatch(removeMembershipTier(tierId))

      if (isRejected(result)) {
        showToast(
          createFailedToast(
            'Unable to delete',
            (result.payload as string) || 'Failed to delete membership tier.',
          ),
        )
        return false
      }

      showToast(
        createSuccessToast(
          'Tier deleted',
          'Membership tier has been removed successfully.',
        ),
      )
      return true
    },
    [dispatch, showToast],
  )

  const toggleTierStatus = useCallback(
    async (tierId: string, enabled: boolean): Promise<boolean> => {
      const result = await dispatch(
        updateMembershipTierStatus({ id: tierId, enabled }),
      )

      if (isRejected(result)) {
        showToast(
          createFailedToast(
            'Unable to update status',
            (result.payload as string) ||
              'Failed to update membership tier status.',
          ),
        )
        return false
      }

      return true
    },
    [dispatch, showToast],
  )

  return {
    tiers,
    loading,
    dataReady,
    saving,
    error,
    refreshTiers,
    saveTier,
    deleteTier,
    toggleTierStatus,
  }
}

export default useMembership
